/**
 * Rock Off Retail — Wishlist v1 page renderer (17 Aug build).
 *
 * Reads window.ROAffinity.get().wishlist (assets/rockoff-affinity.js),
 * resolves each stored {id, handle, hub, ts} entry to real product data via
 * the storefront's own /products/<handle>.js AJAX endpoint — same proven
 * approach as sections/rockoff-recently-viewed.liquid's
 * assets/rockoff-recently-viewed.js (chosen over /search/suggest.json here
 * because a wishlist already has the exact handle for every entry, so a
 * direct per-product fetch is more precise than a free-text search that
 * could match the wrong product or return duplicates) — and renders cards
 * with the theme's existing .ro-card pattern (section-rockoff-product-
 * slider.css, already shipped site-wide).
 *
 * Each card gets a real, working heart toggle built to the exact contract
 * assets/rockoff-affinity.js's bindWishlistButtons() delegated listener
 * expects (data-ro-wishlist-btn + data-ro-wishlist-id/handle/hub/label) —
 * no extra JS needed here for the toggle itself, only for removing the
 * card from view the instant it's un-hearted (a plain toggleWishlist() call
 * elsewhere doesn't know this page's DOM exists).
 *
 * Three states: loading skeleton -> populated grid, or -> friendly empty
 * state (never a broken shell) if the store has nothing saved, every fetch
 * 404s, or localStorage is unavailable (private browsing).
 */
(function () {
  'use strict';

  function formatPrice(cents) {
    var n = parseFloat(cents) / 100;
    if (isNaN(n)) return '';
    // Rock Off Retail UK is GBP-only at this stage — same assumption the
    // rest of this theme's client-rendered price markup makes.
    return '£' + n.toFixed(2);
  }

  function flagFor(product) {
    var tags = Array.isArray(product.tags) ? product.tags : [];
    var hasVault = tags.some(function (t) {
      return String(t).toLowerCase() === 'vault';
    });
    if (hasVault) return 'VAULT';
    var hasNew = tags.some(function (t) {
      return String(t).toLowerCase() === 'new';
    });
    if (hasNew) return 'NEW';
    return '';
  }

  function buildWishlistHeart(entry, product, addLabel, removeLabel) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ro-wishlist-btn ro-wishlist-btn--card is-wishlisted';
    btn.setAttribute('data-ro-wishlist-btn', '');
    btn.setAttribute('data-ro-wishlist-id', entry.id || (product && product.id) || '');
    btn.setAttribute('data-ro-wishlist-handle', entry.handle);
    if (entry.hub) btn.setAttribute('data-ro-wishlist-hub', entry.hub);
    var name = (product && product.title) || entry.handle;
    btn.setAttribute('data-ro-wishlist-label', name);
    // Localised strings, not hardcoded English — passed in from this page's
    // own data-ro-wishlist-add-label/-remove-label attributes (rendered by
    // Liquid from locales/en.default.json `rockoff.wishlist.add`/`.remove`,
    // see sections/rockoff-wishlist.liquid), same locale keys
    // snippets/rockoff-wishlist-button.liquid uses for card/PDP hearts. Also
    // set as data attributes here so assets/rockoff-affinity.js's
    // bindWishlistButtons() can build the SAME localised aria-label after a
    // toggle on this page, exactly as it does for those other hearts.
    var resolvedAddLabel = addLabel || 'Add to wishlist';
    var resolvedRemoveLabel = removeLabel || 'Remove from wishlist';
    btn.setAttribute('data-ro-wishlist-add-label', resolvedAddLabel);
    btn.setAttribute('data-ro-wishlist-remove-label', resolvedRemoveLabel);
    btn.setAttribute('aria-pressed', 'true');
    btn.setAttribute('aria-label', resolvedRemoveLabel + ' ' + name);
    btn.innerHTML =
      '<svg class="ro-wishlist-btn__icon ro-wishlist-btn__icon--empty" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s-7.5-4.6-10-9.1C.4 8.6 2 5 5.6 5c2 0 3.4 1 4.4 2.4C11 6 12.4 5 14.4 5 18 5 19.6 8.6 22 11.9c-2.5 4.5-10 9.1-10 9.1Z"></path></svg>' +
      '<svg class="ro-wishlist-btn__icon ro-wishlist-btn__icon--filled" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 21s-7.5-4.6-10-9.1C.4 8.6 2 5 5.6 5c2 0 3.4 1 4.4 2.4C11 6 12.4 5 14.4 5 18 5 19.6 8.6 22 11.9c-2.5 4.5-10 9.1-10 9.1Z"></path></svg>';
    return btn;
  }

  function buildCard(entry, product, soldOutLabel, addLabel, removeLabel) {
    var a = document.createElement('a');
    a.className = 'ro-card ro-wishlist-page__card';
    a.href = '/products/' + entry.handle;

    var well = document.createElement('span');
    well.className = 'ro-card__well';

    var flag = flagFor(product);
    if (flag) {
      var flagEl = document.createElement('span');
      flagEl.className = 'ro-card__flag';
      flagEl.textContent = flag;
      well.appendChild(flagEl);
    }

    var imgSrc = product.featured_image || (product.images && product.images[0]);
    if (imgSrc) {
      var img = document.createElement('img');
      img.className = 'ro-card__img ro-card__img--primary';
      img.src = imgSrc + (imgSrc.indexOf('?') === -1 ? '?width=500' : '&width=500');
      img.alt = product.title || '';
      img.loading = 'lazy';
      well.appendChild(img);
    }

    well.appendChild(buildWishlistHeart(entry, product, addLabel, removeLabel));
    a.appendChild(well);

    var title = document.createElement('h4');
    title.className = 'ro-card__title';
    title.textContent = product.title || '';
    a.appendChild(title);

    if (product.vendor) {
      var band = document.createElement('span');
      band.className = 'ro-card__band';
      band.textContent = product.vendor;
      a.appendChild(band);
    }

    var priceEl = document.createElement('div');
    priceEl.className = 'ro-card__price';
    if (product.available === false) {
      priceEl.textContent = soldOutLabel || 'Sold out';
      a.classList.add('ro-wishlist-page__card--soldout');
    } else {
      priceEl.textContent = formatPrice(product.price);
    }
    a.appendChild(priceEl);

    // .ro-card-wrap carries the card sizing (flex-basis breakpoints in
    // section-rockoff-product-slider.css) — the 19 Aug refactor moved it
    // off .ro-card, which left these wishlist cards with NO width rule
    // (independent checker finding, 19 Aug: cards sized to their text).
    // Sizing wrapper only — no quick-add icon on the wishlist page, the
    // heart-removal flow is this page's primary action.
    var wrap = document.createElement('div');
    wrap.className = 'ro-card-wrap';
    wrap.appendChild(a);
    return wrap;
  }

  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js')
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function showOnly(refs, key) {
    Object.keys(refs).forEach(function (k) {
      if (!refs[k]) return;
      refs[k].hidden = k !== key;
    });
  }

  function removeCardFromDom(gridEl, handle, refs) {
    var heart = gridEl.querySelector('[data-ro-wishlist-handle="' + CSS.escape(handle) + '"]');
    var card = heart && heart.closest('.ro-wishlist-page__card');
    if (card) card.remove();
    if (!gridEl.querySelector('.ro-wishlist-page__card')) {
      showOnly(refs, 'empty');
    }
  }

  function initSection(section) {
    var refs = {
      loading: section.querySelector('[data-ro-wishlist-loading]'),
      empty: section.querySelector('[data-ro-wishlist-empty]'),
      grid: section.querySelector('[data-ro-wishlist-grid]'),
    };
    var loadErrorEl = section.querySelector('[data-ro-wishlist-load-error]');
    if (!refs.grid) return;
    var soldOutLabel = refs.grid.getAttribute('data-ro-sold-out-label');
    var addLabel = refs.grid.getAttribute('data-ro-wishlist-add-label');
    var removeLabel = refs.grid.getAttribute('data-ro-wishlist-remove-label');

    if (!window.ROAffinity) {
      showOnly(refs, 'empty');
      return;
    }

    var affinity = window.ROAffinity.get();
    var entries = Array.isArray(affinity.wishlist) ? affinity.wishlist : [];
    if (!entries.length) {
      showOnly(refs, 'empty');
      return;
    }

    showOnly(refs, 'loading');

    Promise.all(
      entries.map(function (entry) {
        return fetchProduct(entry.handle).then(function (product) {
          return { entry: entry, product: product };
        });
      })
    )
      .then(function (results) {
        var okCount = 0;
        var failCount = 0;
        results.forEach(function (result) {
          if (result.product) {
            refs.grid.appendChild(buildCard(result.entry, result.product, soldOutLabel, addLabel, removeLabel));
            okCount += 1;
          } else {
            failCount += 1;
          }
        });

        if (okCount === 0) {
          // Every stored handle 404d (deleted/unpublished products) — same
          // friendly empty state, not an error wall.
          showOnly(refs, 'empty');
          return;
        }

        if (failCount > 0 && loadErrorEl) {
          loadErrorEl.hidden = false;
        }
        showOnly(refs, 'grid');
      })
      .catch(function () {
        // Any failure in the chain — friendly empty state, never a broken
        // shell.
        showOnly(refs, 'empty');
      });

    // Un-hearting a card on this page should remove it from view instantly.
    // assets/rockoff-affinity.js's own click handler is delegated on
    // `document` — since this listener sits on a DESCENDANT (`section`),
    // the bubble phase reaches it FIRST, before the document-level handler
    // has actually run toggleWishlist() yet. Deferring the DOM-removal
    // check to a fresh macrotask (setTimeout 0) lets the rest of THIS same
    // click's bubble phase finish first (document's handler included,
    // since bubbling to document happens synchronously within one click
    // dispatch) — by the time the callback below runs, the affinity store
    // has already flipped, so isWishlisted() reads the fresh state.
    section.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('[data-ro-wishlist-btn]');
      if (!btn) return;
      var handle = btn.getAttribute('data-ro-wishlist-handle');
      setTimeout(function () {
        if (!window.ROAffinity.isWishlisted(handle)) {
          removeCardFromDom(refs.grid, handle, refs);
        }
      }, 0);
    });
  }

  function init() {
    var sections = document.querySelectorAll('.ro-wishlist-page');
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
